import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, BadgeCheck, Clock, Store, MessageSquare, Pencil } from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { accountGet, accountSend } from "@/lib/api";

const inp = "w-full bg-white border border-maternnia-nevoa rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto";
const lbl = "block text-xs font-medium text-maternnia-taupe mb-1";

const EDIT_FIELDS = [
  ["categoria", "Categoria"], ["atendimento", "Atendimento"], ["cidade", "Cidade"], ["bairro", "Bairro"],
  ["whatsapp", "WhatsApp"], ["site", "Site"], ["instagram", "Instagram"], ["publico", "Público atendido"],
];

const PainelFornecedorPage = () => {
  const { user, loading, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [forn, setForn] = useState(undefined); // undefined loading, null pending/none, obj
  const [contatos, setContatos] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/entrar", { replace: true }); return; }
    if (user.role !== "fornecedor") { navigate("/minha-conta", { replace: true }); return; }
    accountGet("/account/fornecedor").then((d) => {
      setForn(d || null);
      if (d) setForm({ categoria: d.categoria || "", atendimento: d.atendimento || "", cidade: d.cidade || "", bairro: d.bairro || "", whatsapp: d.whatsapp || "", site: d.site || "", instagram: d.instagram || "", publico: d.publico || "", descricao: d.descricao || "", servicos: d.servicos || "" });
    });
    accountGet("/account/fornecedor/contatos").then((d) => setContatos(d || []));
  }, [user, loading, navigate]);

  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const d = await accountSend("/account/fornecedor", "PATCH", form); setForn(d); toast.success("Perfil atualizado."); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (loading || forn === undefined || !user) {
    return <main className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-maternnia-eucalipto" /></main>;
  }

  return (
    <main data-testid="painel-fornecedor-page" className="bg-maternnia-porcelana/30 min-h-screen">
      <Seo title="Painel do fornecedor" path="/painel-fornecedor" noindex />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium">Painel do fornecedor</h1>
            <p className="text-sm text-maternnia-taupe font-light mt-1">Olá, {user.nome?.split(" ")[0] || "parceiro"}!</p>
          </div>
          <button data-testid="forn-logout" onClick={doLogout} className="inline-flex items-center gap-2 text-sm font-medium text-maternnia-taupe hover:text-maternnia-charcoal border border-maternnia-nevoa rounded-full px-4 py-2 transition">
            <LogOut size={15} /> Sair
          </button>
        </div>

        {!forn ? (
          <div data-testid="forn-pendente" className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-8 text-center">
            <div className="h-14 w-14 mx-auto rounded-2xl bg-amber-50 flex items-center justify-center mb-4"><Clock size={26} className="text-amber-600" /></div>
            <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium">Seu cadastro está em análise</h2>
            <p className="mt-3 text-sm text-maternnia-taupe font-light max-w-md mx-auto">O acesso completo ao painel é liberado após a aprovação da nossa curadoria. Assim que aprovarmos, você poderá gerenciar seu perfil e acompanhar pedidos de contato.</p>
          </div>
        ) : (
          <>
            {/* Status */}
            <div className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-5 mb-6 flex flex-wrap items-center gap-4">
              <span className="h-12 w-12 rounded-2xl bg-maternnia-eucalipto/10 flex items-center justify-center"><Store size={22} className="text-maternnia-eucalipto" /></span>
              <div className="flex-1 min-w-0">
                <h2 className="font-medium text-maternnia-charcoal">{forn.empresa || forn.nome}</h2>
                <p className="text-xs text-maternnia-taupe">{forn.categoria} • {forn.cidade}</p>
              </div>
              <span data-testid="forn-status" className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full"><BadgeCheck size={14} /> Aprovado{forn.verificado ? " • Verificado" : ""}</span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-maternnia-porcelana text-maternnia-taupe px-3 py-1.5 rounded-full">{forn.publicado ? "Publicado" : "Não publicado"}</span>
            </div>

            {/* Edit form */}
            <form onSubmit={save} data-testid="forn-edit-form" className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-6 mb-6">
              <h3 className="font-serif text-xl text-maternnia-charcoal font-medium mb-4 flex items-center gap-2"><Pencil size={16} /> Meu perfil público</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {EDIT_FIELDS.map(([k, label]) => (
                  <div key={k}><label className={lbl}>{label}</label>
                    <input data-testid={`forn-f-${k}`} className={inp} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} /></div>
                ))}
                <div className="sm:col-span-2"><label className={lbl}>Descrição</label>
                  <textarea data-testid="forn-f-descricao" rows={3} className={`${inp} resize-none`} value={form.descricao} onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))} /></div>
                <div className="sm:col-span-2"><label className={lbl}>Serviços</label>
                  <textarea data-testid="forn-f-servicos" rows={2} className={`${inp} resize-none`} value={form.servicos} onChange={(e) => setForm((f) => ({ ...f, servicos: e.target.value }))} /></div>
              </div>
              <button data-testid="forn-save" disabled={busy} className="mt-5 inline-flex items-center justify-center gap-2 bg-maternnia-eucalipto text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-maternnia-floresta transition disabled:opacity-70">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Salvar alterações"}
              </button>
            </form>

            {/* Contatos */}
            <div data-testid="forn-contatos" className="bg-white rounded-2xl border border-maternnia-nevoa/60 p-6">
              <h3 className="font-serif text-xl text-maternnia-charcoal font-medium mb-4 flex items-center gap-2"><MessageSquare size={16} /> Pedidos de contato</h3>
              {contatos.length === 0 ? (
                <p className="text-sm text-maternnia-taupe font-light">Nenhum pedido de contato ainda. Eles aparecem aqui quando famílias demonstram interesse no seu perfil.</p>
              ) : (
                <ul className="divide-y divide-maternnia-nevoa/50">
                  {contatos.map((c) => (
                    <li key={c.id} className="py-3">
                      <p className="text-sm font-medium text-maternnia-charcoal">{c.nome || "—"} <span className="text-maternnia-taupe font-light">• {c.email}</span></p>
                      {c.mensagem && <p className="text-sm text-maternnia-taupe font-light mt-1">{c.mensagem}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default PainelFornecedorPage;
